/**
 * Collapsible tracking-provider section used inside the Builder's
 * Analytics & Tracking step.
 *
 * Each card has:
 *   - A brand-coloured logo chip on the left
 *   - The provider name + an optional "active" pill when configured
 *   - A right-side control: either a chevron (collapsible) or an
 *     enable toggle (gating)
 *
 * Two interaction modes:
 *
 *   1. mode="collapse" (default) — click anywhere on the header to
 *      toggle expand/collapse. Use for sections that are always
 *      available but visually large.
 *
 *   2. mode="toggle" — the header shows an enable switch instead of a
 *      chevron, and the body is only mounted when `enabled === true`.
 *      Use for opt-in providers like Meta CAPI where we don't want any
 *      child UI shown until the user explicitly turns it on.
 */

import React from 'react';
import { ChevronDown } from 'lucide-react';
import { ToggleSwitch } from '../Common/ToggleSwitch';

interface BaseProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  /** Show a small green "Active" pill next to the title. */
  active?: boolean;
  children: React.ReactNode;
}

interface CollapseProps extends BaseProps {
  mode?: 'collapse';
  defaultOpen?: boolean;
}

interface ToggleProps extends BaseProps {
  mode: 'toggle';
  enabled: boolean;
  onEnabledChange: (next: boolean) => void;
}

type Props = CollapseProps | ToggleProps;

export const TrackingSection: React.FC<Props> = (props) => {
  const isToggleMode = props.mode === 'toggle';
  const [open, setOpen] = React.useState<boolean>(
    isToggleMode ? false : (props as CollapseProps).defaultOpen ?? false
  );

  const expanded = isToggleMode ? (props as ToggleProps).enabled : open;

  const headerClickable = !isToggleMode;

  return (
    <div className="rounded-xl border border-gray-700 bg-gray-800/40 overflow-hidden transition-colors hover:border-gray-600">
      <button
        type="button"
        onClick={headerClickable ? () => setOpen((o) => !o) : undefined}
        // For toggle-mode the header shouldn't behave like a button —
        // the dedicated switch on the right is the only control.
        tabIndex={headerClickable ? 0 : -1}
        className={
          'w-full flex items-center gap-3 px-4 py-3 text-left ' +
          (headerClickable
            ? 'cursor-pointer hover:bg-gray-800/60'
            : 'cursor-default')
        }
        aria-expanded={expanded}
      >
        {props.icon}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-white">{props.title}</span>
            {props.active && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded bg-emerald-500/15 text-emerald-300">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                Active
              </span>
            )}
          </div>
          {props.description && (
            <p className="text-xs text-gray-400 mt-0.5 truncate">{props.description}</p>
          )}
        </div>

        {isToggleMode ? (
          // Stop click propagation so the underlying button doesn't
          // also try to flip state. ToggleSwitch already handles
          // keyboard activation on its own.
          <span onClick={(e) => e.stopPropagation()}>
            <ToggleSwitch
              checked={(props as ToggleProps).enabled}
              onChange={(props as ToggleProps).onEnabledChange}
            />
          </span>
        ) : (
          <ChevronDown
            size={18}
            className={
              'text-gray-400 transition-transform flex-shrink-0 ' +
              (expanded ? 'rotate-180' : '')
            }
          />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-700/60 bg-gray-900/30">
          {props.children}
        </div>
      )}
    </div>
  );
};
